import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroTriptych.css';

const panels = [
  {
    image: '/images/IMG_4683.jpeg',
    label: 'Destruction',
    alt: 'Elijah Moreno research on tribal termination and institutional destruction in federal Indian policy',
    color: 'var(--color-destruction)',
    chapter: 'price-of-delay',
    subtitle: 'The Price of Delay',
    text: 'When governance is removed, economic damage is immediate and persistent. The dissolution of tribal governments during the Termination Era caused population loss and long-run economic decline that compounds across generations.',
    hoverText: 'These policies dismantle governance, removing institutions, authority, and the capacity to act.',
  },
  {
    image: '/images/nurse-holding-baby-ihs.jpeg',
    label: 'Trust',
    alt: 'Nurse holding baby at Public Health Service Indian Hospital, Elijah Moreno research on institutional trust',
    color: 'var(--color-trust)',
    chapter: 'returns-to-trust',
    subtitle: 'The Returns to Trust',
    text: 'Even when institutions exist, they only generate returns when communities trust them. Public infrastructure increases outcomes such as vaccination, but not where institutional history has destroyed trust. Who delivers policy matters as much as what is delivered.',
    hoverText: 'These policies structure obligation without full autonomy, stabilizing governance while maintaining dependency.',
  },
  {
    image: '/images/IMG_4756.jpeg',
    label: 'Partnership',
    alt: 'Elijah Moreno research on tribal partnership and political incorporation policy',
    color: 'var(--color-partnership)',
    chapter: '',
    subtitle: 'Partnership',
    text: 'These policies position governments as actors, enabling negotiation, coordination, and political influence.',
    hoverText: 'These policies position governments as actors, enabling negotiation, coordination, and political influence.',
  },
  {
    image: '/images/IMG_4685.jpeg',
    label: 'Restoration',
    alt: 'Elijah Moreno research on tribal land consolidation and governance restoration',
    color: 'var(--color-restoration)',
    chapter: 'remedy-of-the-commons',
    subtitle: 'The Remedy of the Commons',
    text: 'Rebuilding governance restores economic capacity, but only when institutions have real authority. Returning land or resources produces meaningful effects when decision-making power is restored, and limited effects when it is not.',
    hoverText: 'These policies rebuild the material foundations of governance, returning land, resources, and jurisdiction.',
  },
];

function throttle(fn, ms) {
  let last = 0;
  return function (...args) {
    const now = Date.now();
    if (now - last >= ms) {
      last = now;
      fn.apply(this, args);
    }
  };
}

export default function HeroTriptych() {
  const navigate = useNavigate();
  const panelsRef = useRef([]);
  const [loadedImages, setLoadedImages] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hero__panel--visible');
          }
        });
      },
      { threshold: 0.2 }
    );

    panelsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleClick = (panel) => {
    if (panel.chapter) {
      navigate(`/research#${panel.chapter}`);
    } else {
      navigate('/research');
    }
  };

  const handleKeyDown = (e, panel) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(panel);
    }
  };

  const throttledMove = useRef(
    throttle((e, cardEl) => {
      if (!cardEl) return;
      const rect = cardEl.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      cardEl.style.transform = `perspective(600px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`;
    }, 16)
  );

  const handleMouseMove = useCallback((e, cardEl) => {
    if (typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches) return;
    throttledMove.current(e, cardEl);
  }, []);

  const handleMouseLeave = useCallback((cardEl) => {
    if (!cardEl) return;
    cardEl.style.transform = '';
  }, []);

  const handleImageLoad = (index) => {
    setLoadedImages(prev => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  return (
    <section className="hero">
      <div className="hero__panels container">
        {panels.map((panel, i) => (
          <div
            key={panel.label}
            className="hero__panel"
            ref={(el) => (panelsRef.current[i] = el)}
            style={{ transitionDelay: `${i * 150}ms` }}
            onClick={() => handleClick(panel)}
            onKeyDown={(e) => handleKeyDown(e, panel)}
            onMouseMove={(e) => handleMouseMove(e, panelsRef.current[i]?.querySelector('.hero__card-front'))}
            onMouseLeave={() => handleMouseLeave(panelsRef.current[i]?.querySelector('.hero__card-front'))}
            onTouchEnd={() => handleMouseLeave(panelsRef.current[i]?.querySelector('.hero__card-front'))}
            role="link"
            tabIndex={0}
            aria-label={`${panel.label}: ${panel.subtitle}. ${panel.hoverText}`}
          >
            <div className="hero__card">
              <div className="hero__card-front">
                {!loadedImages.has(i) && <div className="hero__skeleton" />}
                <img
                  src={panel.image}
                  alt={panel.alt}
                  className={`hero__image ${loadedImages.has(i) ? 'hero__image--loaded' : ''}`}
                  loading={i < 2 ? 'eager' : 'lazy'}
                  fetchpriority={i < 2 ? 'high' : undefined}
                  width={800}
                  height={600}
                  onLoad={() => handleImageLoad(i)}
                />
                <div className="hero__overlay" style={{ '--panel-color': panel.color }} />
                <span className="hero__hover-text">{panel.hoverText}</span>
                <span className="hero__label">{panel.label}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
